import { Component, effect, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { MenuItem } from 'primeng/api';

import { AppMenuitem } from './app.menuitem';
import { AuthService } from '@/pages/service/core/auth.service';
import { SubscriptionService } from '@/pages/service/core/subscription.service';
import { AttendanceService } from '@/pages/attendance/attendance.service';

import { hasAnyRole } from './role.util';
import { TOP_MENU_CATALOG } from './submenu-panel/submenu-catalog';
import { TopMenuEntry } from './submenu-panel/submenu-model';
import { SubmenuStateService } from './submenu-panel/submenu-state.service';
import { PermissionStore } from '@/pages/service/core/permission-store';
import { ApiService } from '@/pages/service/core/api.service';
import { SeoStatusService } from '@/pages/seo/seo-status.service';
import { VerticalConfigService } from '@/pages/service/core/vertical-config.service';

@Component({
    selector: 'app-menu',
    standalone: true,
    imports: [CommonModule, AppMenuitem, RouterModule],
    template: `
    <ul class="layout-menu">
      <ng-container *ngFor="let item of model; let i = index">
        <li app-menuitem
            *ngIf="!item.separator"
            [item]="item"
            [index]="i"
            [root]="true">
        </li>
        <li *ngIf="item.separator" class="menu-separator"></li>
      </ng-container>
    </ul>
  `
})
export class AppMenu implements OnInit, OnDestroy {

    private auth   = inject(AuthService);
    private router = inject(Router);
    private subs   = inject(SubscriptionService);
    private state  = inject(SubmenuStateService);
    private perms  = inject(PermissionStore);
    private seoStatus = inject(SeoStatusService);
    private attendance = inject(AttendanceService);
    private verticalConfig = inject(VerticalConfigService);
    private api = inject(ApiService);

    model: MenuItem[] = [];

    constructor() {
        // "Pro version" mode hides several top-level entries (Field Tracking, Attendance,
        // Inbox, Analytics, Automation, Operations, Database). proMode resolves from the
        // app-init fetch and may flip after first paint (stale/absent cache), so rebuild
        // the sidebar whenever it changes.
        effect(() => {
            this.verticalConfig.proMode();   // track the signal
            this.model = this.buildMenu();
        });
    }

    // Live unread-inbox count → the Inbox menu badge. Polled every 60s for the roles
    // that can see the Inbox entry; 0 hides the badge.
    private inboxUnread = 0;
    private inboxPollTimer: any = null;

    // Field attendance — drives the Check in / Check out item above Logout (agents).
    attSession: any = null;
    attBusy = false;
    get attCheckedIn(): boolean { return this.attSession?.status === 'OPEN' && !!this.attSession?.id; }
    private seoEnabled: boolean | null = null;   // null = not loaded; SEO entry hidden until true
    private subscription = new Subscription();

    ngOnInit(): void {
        this.model = this.buildMenu();

        // Agents get a Check in / Check out control above Logout — load current state.
        if (hasAnyRole('ROLE_AGENT')) { this.loadAttendance(); }

        // Inbox unread badge — poll for the roles that can see the entry.
        if (hasAnyRole('ROLE_COMPANY_ADMIN', 'ROLE_MANAGER')) {
            this.pollInboxUnread();
            this.inboxPollTimer = setInterval(() => this.pollInboxUnread(), 60_000);
        }

        this.subscription.add(
            this.subs.subscription$.subscribe(() => this.model = this.buildMenu())
        );

        this.subscription.add(
            this.perms.changed$.subscribe(() => this.model = this.buildMenu())
        );

        // SEO Engine is a per-company add-on — only show it once the super admin enables it.
        if (hasAnyRole('ROLE_COMPANY_ADMIN', 'ROLE_MANAGER')) {
            this.seoStatus.load();
        }
        this.subscription.add(
            this.seoStatus.enabled$.subscribe(v => { this.seoEnabled = v; this.model = this.buildMenu(); })
        );

        this.subscription.add(
            this.router.events
                .pipe(filter(e => e instanceof NavigationEnd))
                .subscribe((e: any) => this.syncPanelToUrl((e as NavigationEnd).urlAfterRedirects))
        );

        this.syncPanelToUrl(this.router.url);
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
        if (this.inboxPollTimer) clearInterval(this.inboxPollTimer);
    }

    /** Refresh the Inbox unread badge — best-effort, silent on failure. */
    private pollInboxUnread(): void {
        this.api.get<any>('/api/conversations/unread-total').subscribe({
            next: (res: any) => {
                const n = Number(res?.data ?? res ?? 0) || 0;
                if (n !== this.inboxUnread) {
                    this.inboxUnread = n;
                    this.model = this.buildMenu();   // re-stamp the badge
                }
            },
            error: () => { /* badge is best-effort */ },
        });
    }

    /**
     * Flatten the top-level catalog into PrimeNG MenuItems so the existing
     * AppMenuitem renderer (and therefore the original styling) keeps working.
     * Entries are grouped by their `group` field — each group becomes a root
     * with its name as the section heading. Settings is extracted and pinned
     * with Logout at the bottom of the sidebar.
     */
    private buildMenu(): MenuItem[] {
        const proMode = this.verticalConfig.proMode();
        const allowed = TOP_MENU_CATALOG
            .filter(entry => !entry.roles || hasAnyRole(...entry.roles))
            .filter(entry => entry.id !== 'seo' || this.seoEnabled === true)  // hide SEO until enabled for the company
            .filter(entry => !(entry.proHidden === true && proMode))          // Pro mode hides admin/tracking entries
            .filter(entry => this.entryVisible(entry));
        // Settings is reached from the top-right account menu, not the sidebar — keep it out of the list.
        // Assistant is pulled out too: it is pinned to the BOTTOM of the sidebar below, since it
        // assists with every section rather than belonging to any one of them. Its catalog entry
        // stays put so route matching / active highlighting still work.
        const topEntries = allowed.filter(e => e.id !== 'settings' && e.id !== 'assistant');

        // Group entries by their `group` field while preserving catalog order.
        const groupOrder: string[] = [];
        const grouped = new Map<string, TopMenuEntry[]>();
        for (const entry of topEntries) {
            const key = entry.group ?? '';
            if (!grouped.has(key)) {
                grouped.set(key, []);
                groupOrder.push(key);
            }
            grouped.get(key)!.push(entry);
        }

        const topRoots: MenuItem[] = groupOrder.map(key => {
            const entries = grouped.get(key) ?? [];
            const items = entries.map(e => this.toMenuItem(e)).filter((i): i is MenuItem => !!i);
            return { label: key, items };
        }).filter(root => (root.items?.length ?? 0) > 0);

        // Settings + Logout now live in the top-right account menu (see app.user-menu).
        // Pinned at the bottom: the AI assistant, and the agents' Check in / Check out control.
        const bottomItems: MenuItem[] = [];

        // Taken from `allowed`, so the same role + screen-permission filtering the rest of the
        // sidebar goes through still applies — a restricted user who can't reach /assistant
        // simply has no entry here.
        const assistantEntry = allowed.find(e => e.id === 'assistant');
        if (assistantEntry) {
            const item = this.toMenuItem(assistantEntry);
            if (item) bottomItems.push(item);
        }

        if (hasAnyRole('ROLE_AGENT')) {
            bottomItems.push({
                label: this.attBusy ? 'Please wait…' : (this.attCheckedIn ? 'Check out' : 'Check in'),
                icon: 'pi pi-fw pi-map-marker',
                // Not checked in → danger style so a logged-in agent is nudged to check in.
                styleClass: this.attCheckedIn ? 'menu-checkout' : 'menu-checkin menu-checkin-danger',
                command: () => this.toggleAttendance()
            });
        }

        return [
            ...topRoots,
            ...(bottomItems.length ? [{ separator: true } as MenuItem, { label: '', items: bottomItems } as MenuItem] : [])
        ];
    }

    /** Whether the entry should appear, given the user's screen permissions. Uses the STRICT
     *  {@code menuAllowed} gate (not canVisit), so a restricted user's sidebar only shows entries
     *  whose screen is explicitly granted — unmapped/ungranted entries (Field Tracking, Attendance,
     *  Logs, Lead Import, and groups whose only visible item is one of those) are hidden. */
    private entryVisible(entry: TopMenuEntry): boolean {
        if (this.perms.unrestricted) return true;
        if (entry.routerLink) {
            return this.perms.menuAllowed(this.entryUrl(entry.routerLink, entry.queryParams));
        }
        if (entry.sections && entry.sections.length > 0) {
            // A group shows only if at least one of its items is an explicitly-granted screen.
            return entry.sections.some(sec => sec.items.some(it =>
                (!it.roles || hasAnyRole(...it.roles)) && this.perms.menuAllowed(this.entryUrl(it.routerLink, it.queryParams))));
        }
        return true;
    }

    private entryUrl(routerLink: any[], queryParams?: any): string {
        const path = '/' + (routerLink || []).join('/').replace(/^\/+/, '');
        if (queryParams) {
            const qp = new URLSearchParams(queryParams).toString();
            return qp ? path + '?' + qp : path;
        }
        return path;
    }

    /** Display label for an entry — swaps to its Pro alias (e.g. "My Work") in Pro mode. */
    private labelFor(entry: TopMenuEntry): string {
        return this.verticalConfig.proMode() && entry.proLabel ? entry.proLabel : entry.label;
    }

    private toMenuItem(entry: TopMenuEntry): MenuItem | null {
        const icon = entry.icon.startsWith('pi pi-fw') ? entry.icon : `pi pi-fw ${entry.icon.replace(/^pi\s+/, '')}`;
        const active = this.isEntryActive(entry, this.router.url);

        if (entry.routerLink) {
            // For direct entries with query params, navigate via command so the
            // query params are applied and the route-active highlight stays
            // unambiguous when multiple entries share a path (e.g. Chatbot vs
            // Settings both pointing to `/settings`).
            if (entry.queryParams) {
                return {
                    label: this.labelFor(entry),
                    icon,
                    styleClass: active ? 'active-route' : undefined,
                    command: () => this.router.navigate(entry.routerLink!, { queryParams: entry.queryParams })
                };
            }
            return {
                label: this.labelFor(entry),
                icon,
                routerLink: entry.routerLink,
                // Inbox carries the live UNREAD count (polled) — "3 customers replied".
                badge: entry.id === 'inbox' && this.inboxUnread > 0
                    ? (this.inboxUnread > 99 ? '99+' : String(this.inboxUnread))
                    : undefined
            };
        }

        if (entry.sections && entry.sections.length > 0) {
            return {
                label: this.labelFor(entry),
                icon,
                styleClass: active ? 'active-route' : undefined,
                command: () => this.openGroup(entry)
            };
        }

        return null;
    }

    private openGroup(entry: TopMenuEntry): void {
        const open = this.state.snapshot();
        if (open?.id === entry.id) {
            this.state.close();
            return;
        }
        this.state.open(entry);

        const firstItem = entry.sections?.[0]?.items?.find(i => !i.roles || hasAnyRole(...i.roles));
        if (firstItem) {
            this.router.navigate(firstItem.routerLink, { queryParams: firstItem.queryParams });
        }
    }

    private syncPanelToUrl(url: string): void {
        const pathPart = url.split('?')[0].split('#')[0];
        const params = this.parseQuery(url);
        const match = TOP_MENU_CATALOG
            .filter(e => !e.roles || hasAnyRole(...e.roles))
            .find(e => (e.matchPrefixes ?? []).some(p => this.matchesPrefix(p, pathPart, params)));

        if (!match) {
            this.state.close();
            return;
        }
        if (match.sections && this.state.snapshot()?.id !== match.id) {
            this.state.open(match);
        } else if (!match.sections) {
            this.state.close();
        }
        // Refresh the model so the active group reflects its highlighted state.
        this.model = this.buildMenu();
    }

    private isEntryActive(entry: TopMenuEntry, url: string): boolean {
        const pathPart = url.split('?')[0].split('#')[0];
        const params = this.parseQuery(url);
        return (entry.matchPrefixes ?? []).some(p => this.matchesPrefix(p, pathPart, params));
    }

    /**
     * Matches a `matchPrefix` against the current URL parts. The prefix can
     * include a `?key=value&...` fragment for query-param-specific matching.
     * A more specific prefix (with required query params) only matches when
     * those params are present and equal.
     */
    private matchesPrefix(prefix: string, path: string, queryParams: Record<string, string>): boolean {
        const [prefixPath, prefixQuery] = prefix.split('?');
        const pathMatches = path === prefixPath || path.startsWith(prefixPath + '/');
        if (!pathMatches) return false;
        if (!prefixQuery) return true;
        const required = new URLSearchParams(prefixQuery);
        for (const [k, v] of required) {
            if (queryParams[k] !== v) return false;
        }
        return true;
    }

    private parseQuery(url: string): Record<string, string> {
        const i = url.indexOf('?');
        if (i < 0) return {};
        const out: Record<string, string> = {};
        const queryPart = url.substring(i + 1).split('#')[0];
        new URLSearchParams(queryPart).forEach((v, k) => { out[k] = v; });
        return out;
    }

    /** Load the agent's current attendance session, then refresh the menu so the
     *  Check in / Check out label reflects reality. */
    private loadAttendance(): void {
        this.attendance.current().subscribe({
            next: (r: any) => { this.attSession = (r?.data ?? r) || null; this.model = this.buildMenu(); },
            error: () => { this.attSession = null; this.model = this.buildMenu(); }
        });
    }

    /** Check in (or out) using the browser's location — same flow as the dashboard. */
    toggleAttendance(): void {
        if (this.attBusy) { return; }
        this.attBusy = true;
        this.model = this.buildMenu();
        const done = () => { this.attBusy = false; this.loadAttendance(); };
        const fail = () => { this.attBusy = false; this.model = this.buildMenu(); };
        const withPos = (cb: (lat?: number, lng?: number) => void) => {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    (p) => cb(p.coords.latitude, p.coords.longitude),
                    () => cb(undefined, undefined),
                    { timeout: 8000 }
                );
            } else { cb(undefined, undefined); }
        };
        if (this.attCheckedIn) {
            const id = this.attSession.id;
            withPos((lat, lng) => this.attendance.checkOut(id, lat, lng).subscribe({ next: done, error: fail }));
        } else {
            withPos((lat, lng) => this.attendance.checkIn(lat, lng).subscribe({ next: done, error: fail }));
        }
    }

    logout(): void {
        this.auth.logout();
        this.router.navigate(['/auth/login']);
    }
}
